/**
 * QueryBuilder - The fluent query builder.
 */
import { backendDerivationRoot } from "../../backend/derive-backend";
import {
  type GraphDef,
  type GraphIdentityConfig,
} from "../../core/define-graph";
import {
  resolveRuntimeKindInput,
  type RuntimeEdgeKind,
  type RuntimeEdgeTypeFor,
  type RuntimeNodeKind,
  type RuntimeNodeTypeFor,
} from "../../core/runtime-kind";
import {
  coordinateContext,
  describeCoordinate,
  resolveReadCoordinate,
} from "../../core/temporal";
import {
  type EdgeType,
  type NodeType,
  type TemporalMode,
} from "../../core/types";
import { ConfigurationError, KindNotFoundError } from "../../errors";
import { isInteropProbeKey } from "../../utils/object";
import {
  type AggregateExpr,
  type FieldRef,
  type GroupBySpec,
  type HybridFusionOptions,
  mergeEdgeKinds,
  type PredicateExpression,
  type ProjectedField,
  type SortDirection,
  type TraversalDirection,
  type TraversalExpansion,
} from "../ast";
import { validateAggregateOperand } from "../compiler/aggregate-validation";
import {
  createOuterReferenceExpression,
  type DatabaseExpression,
} from "../expressions";
import { jsonPointer, parseJsonPointer } from "../json-pointer";
import {
  buildFieldBuilderForTypeInfo,
  createFulltextAccessor,
  fieldRef,
  type Predicate,
  stringField,
} from "../predicates";
import {
  type FieldTypeInfo,
  type SchemaIntrospector,
} from "../schema-introspector";
import { buildQueryAst } from "./ast-builder";
import {
  createDynamicFieldBuilder,
  type DynamicEdgeType,
  type DynamicNodeType,
} from "./dynamic";
import { ExecutableAggregateQuery } from "./executable-aggregate-query";
import {
  type DatabaseProjection,
  ExecutableProjectionQuery,
} from "./executable-projection-query";
import { ExecutableQuery } from "./executable-query";
import {
  createExpressionAliasContext,
  type ExpressionAliasContext,
  type QueryExpressionContext,
} from "./expression-context";
import {
  assertExpressionScope,
  getExpressionScope,
  isDatabaseExpression,
} from "./expression-scope";
import { createExpressionSubqueryHelpers } from "./expression-subqueries";
import { registerQueryBuilderInternalContext } from "./internal-context";
import { getQueryBuilderInternalContext } from "./internal-context";
import {
  assertSharedNodeField,
  buildOrderSpec,
  resolveSystemOrderField,
} from "./order-by-field";
import { withRecursiveStopExpansion } from "./recursive-stop";
import { executeQueryTerminal } from "./terminal-query";
import { TraversalBuilder } from "./traversal-builder";
import {
  type AliasMap,
  type BaseFieldAccessor,
  type EdgeAccessor,
  type EdgeAlias,
  type EdgeAliasMap,
  type EmptyAliasMap,
  type EmptyEdgeAliasMap,
  type EmptyRecursiveAliasMap,
  type NodeAccessor,
  type NodeAlias,
  type QueryBuilderConfig,
  type QueryBuilderState,
  type QueryCoordinateState,
  type RecursiveAliasMap,
  type SelectContext,
  type UniqueAlias,
} from "./types";
import {
  validateHybridFusionOptions,
  validateQueryRange,
  validateQuerySource,
  validateQueryState,
  validateSortDirection,
  validateSqlIdentifier,
  validateTraversalOptions,
} from "./validation";

function resolveAggregateFieldTypeInfo(
  introspector: SchemaIntrospector,
  nodeKindNames: readonly string[] | undefined,
  edgeKindNames: readonly string[] | undefined,
  path: readonly string[],
): FieldTypeInfo | undefined {
  const [propertyName, ...nestedPath] = path;
  if (propertyName === undefined) return undefined;

  const rootTypeInfo =
    nodeKindNames === undefined ?
      edgeKindNames === undefined ?
        undefined
      : introspector.getSharedEdgeFieldTypeInfo(edgeKindNames, propertyName)
    : introspector.getSharedFieldTypeInfo(nodeKindNames, propertyName);

  function resolveNested(
    current: FieldTypeInfo | undefined,
    index: number,
  ): FieldTypeInfo | undefined {
    const segment = nestedPath[index];
    return segment === undefined ? current : (
        resolveNested(current?.shape?.[segment], index + 1)
      );
  }

  return resolveNested(rootTypeInfo, 0);
}

/**
 * Identity-aware traversal option, available only on a graph that declares an
 * identity configuration. On any other graph the property is typed `never`, so
 * setting it is a compile error (and a runtime guard rejects it as well).
 *
 * When `includeIdentityMembers` is true, the traversal's source hop matches an
 * edge attached to *any* coordinate-visible member of the source node's
 * identity class, not just the source node itself. Semantics:
 *
 * - Results are physical rows: the nodes and edges returned are the ones
 *   actually stored, never a synthesized merge of the class.
 * - Identity-class membership is resolved at the query's own coordinate, so a
 *   traversal under `asOf`/`asOfRecorded` follows only the assertions that were
 *   in force at that instant; a retracted assertion stops conducting.
 * - Within a step, physical edge ids are deduplicated. The one exception is a
 *   self-inverse edge between two folded peers (same id, different kind), which
 *   legitimately matches in both directions and is kept.
 * - Under recursion, cycle detection keys on (kind, id) rather than id alone,
 *   so passing through two folded peers is not mistaken for a revisit. Path
 *   output is unaffected: it remains an array of bare node ids.
 */
export type IdentityTraversalOption<G extends GraphDef> =
  G["identity"] extends GraphIdentityConfig ?
    Readonly<{ includeIdentityMembers?: boolean }>
  : Readonly<{ includeIdentityMembers?: never }>;

type DynamicNodeTypeFor<T> =
  T extends RuntimeNodeKind ? RuntimeNodeTypeFor<T> : DynamicNodeType;

type DynamicEdgeTypeFor<T> =
  T extends RuntimeEdgeKind ? RuntimeEdgeTypeFor<T> : DynamicEdgeType;

/**
 * Builds projected fields for a node alias (including all metadata columns).
 */
function buildNodeFields(alias: string): ProjectedField[] {
  return [
    {
      outputName: `${alias}_id`,
      source: fieldRef(alias, ["id"]),
    },
    {
      outputName: `${alias}_kind`,
      source: fieldRef(alias, ["kind"]),
    },
    {
      outputName: `${alias}_props`,
      source: fieldRef(alias, ["props"]),
    },
    {
      outputName: `${alias}_version`,
      source: fieldRef(alias, ["version"]),
    },
    {
      outputName: `${alias}_valid_from`,
      source: fieldRef(alias, ["valid_from"]),
    },
    {
      outputName: `${alias}_valid_to`,
      source: fieldRef(alias, ["valid_to"]),
    },
    {
      outputName: `${alias}_created_at`,
      source: fieldRef(alias, ["created_at"]),
    },
    {
      outputName: `${alias}_updated_at`,
      source: fieldRef(alias, ["updated_at"]),
    },
    {
      outputName: `${alias}_deleted_at`,
      source: fieldRef(alias, ["deleted_at"]),
    },
  ];
}

/**
 * Builds projected fields for an edge alias (including all metadata columns).
 *
 * Edge columns are stored in the traversal's node CTE (e.g., cte_c contains e_id, e_kind, etc.).
 * The nodeCteAlias parameter specifies which CTE contains these columns.
 */
function buildEdgeFields(
  edgeAlias: string,
  nodeCteAlias: string,
): ProjectedField[] {
  const cteAlias = `cte_${nodeCteAlias}`;
  return [
    {
      outputName: `${edgeAlias}_id`,
      source: fieldRef(edgeAlias, ["id"]),
      cteAlias,
    },
    {
      outputName: `${edgeAlias}_kind`,
      source: fieldRef(edgeAlias, ["kind"]),
      cteAlias,
    },
    {
      outputName: `${edgeAlias}_from_id`,
      source: fieldRef(edgeAlias, ["from_id"]),
      cteAlias,
    },
    {
      outputName: `${edgeAlias}_to_id`,
      source: fieldRef(edgeAlias, ["to_id"]),
      cteAlias,
    },
    {
      outputName: `${edgeAlias}_props`,
      source: fieldRef(edgeAlias, ["props"]),
      cteAlias,
    },
    {
      outputName: `${edgeAlias}_valid_from`,
      source: fieldRef(edgeAlias, ["valid_from"]),
      cteAlias,
    },
    {
      outputName: `${edgeAlias}_valid_to`,
      source: fieldRef(edgeAlias, ["valid_to"]),
      cteAlias,
    },
    {
      outputName: `${edgeAlias}_created_at`,
      source: fieldRef(edgeAlias, ["created_at"]),
      cteAlias,
    },
    {
      outputName: `${edgeAlias}_updated_at`,
      source: fieldRef(edgeAlias, ["updated_at"]),
      cteAlias,
    },
    {
      outputName: `${edgeAlias}_deleted_at`,
      source: fieldRef(edgeAlias, ["deleted_at"]),
      cteAlias,
    },
  ];
}

type TemporalMethod<
  G extends GraphDef,
  Aliases extends AliasMap,
  EdgeAliases extends EdgeAliasMap,
  RecursiveAliases extends RecursiveAliasMap,
  CoordinateState extends QueryCoordinateState,
> =
  CoordinateState extends "open" ?
    (
      mode: TemporalMode,
      asOf?: string,
    ) => QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, "open">
  : never;

/**
 * The fluent query builder.
 *
 * Type parameters accumulate as methods are chained:
 * - G: The graph definition
 * - Aliases: Map of alias names to their node kinds
 * - EdgeAliases: Map of alias names to their edge kinds (accumulated during traversals)
 */
export class QueryBuilder<
  G extends GraphDef,
  Aliases extends AliasMap = EmptyAliasMap,
  EdgeAliases extends EdgeAliasMap = EmptyEdgeAliasMap,
  RecursiveAliases extends RecursiveAliasMap = EmptyRecursiveAliasMap,
  CoordinateState extends QueryCoordinateState = "open",
> {
  readonly #config: QueryBuilderConfig;
  readonly #state: QueryBuilderState;
  readonly temporal: TemporalMethod<
    G,
    Aliases,
    EdgeAliases,
    RecursiveAliases,
    CoordinateState
  >;

  constructor(config: QueryBuilderConfig, state: QueryBuilderState) {
    validateQueryState(state);
    this.#config = config;
    this.#state = state;
    this.temporal = ((mode, asOf) =>
      this.#setTemporal(mode, asOf)) as TemporalMethod<
      G,
      Aliases,
      EdgeAliases,
      RecursiveAliases,
      CoordinateState
    >;
  }

  /**
   * Sets temporal mode.
   *
   * @param mode - The temporal mode to use
   * @param asOf - Required timestamp for "asOf" mode (ISO 8601 string).
   *   Rejected for every other mode — pinning an instant is only meaningful
   *   in "asOf" mode, so `temporal("current", t)` is a caller error, not a
   *   silently-dropped argument.
   * @throws ValidationError if mode is "asOf" but no timestamp is provided, or
   *   if an asOf is supplied with a non-"asOf" mode.
   */
  #setTemporal(
    mode: TemporalMode,
    asOf?: string,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, "open"> {
    const { sealedCoordinate } = getQueryBuilderInternalContext(this.#config);
    if (sealedCoordinate !== undefined) {
      const coordinate = sealedCoordinate;
      throw new ConfigurationError(
        `.temporal() is not available on a StoreView query — the view's ` +
          `temporal coordinate (${describeCoordinate(coordinate)}) is sealed. ` +
          `Re-coordinate on the live Store via store.query() or store.view(...).`,
        {
          code: "STORE_VIEW_SEALED_QUERY",
          ...coordinateContext(coordinate),
          requestedMode: mode,
        },
      );
    }
    const coordinate = resolveReadCoordinate(
      mode,
      asOf,
      `Use .temporal("asOf", "2024-01-15T10:00:00.000Z") or .temporal("current") for current time.`,
    );
    return new QueryBuilder(this.#config, {
      ...this.#state,
      temporalMode: coordinate.valid.mode,
      asOf: coordinate.valid.asOf,
    });
  }

  /**
   * Starts a query from one kind or a nonempty explicit list of kinds.
   * Lists scan exactly the requested kinds; duplicates are normalized.
   * Properties used in predicates and expressions must be shared by all kinds.
   *
   * @param kind - The node kind to start from
   * @param alias - A unique alias for this node (compile-time error if duplicate)
   */
  from<
    const Kinds extends readonly [
      keyof G["nodes"] & string,
      ...(keyof G["nodes"] & string)[],
    ],
    A extends string,
  >(
    kinds: Kinds,
    alias: UniqueAlias<A, Aliases>,
  ): QueryBuilder<
    G,
    Aliases & Record<A, NodeAlias<G["nodes"][Kinds[number]]["type"]>>,
    EdgeAliases,
    RecursiveAliases,
    CoordinateState
  >;

  from<K extends keyof G["nodes"] & string, A extends string>(
    kind: K,
    alias: UniqueAlias<A, Aliases>,
    options?: { includeSubClasses?: false },
  ): QueryBuilder<
    G,
    Aliases & Record<A, NodeAlias<G["nodes"][K]["type"]>>,
    EdgeAliases,
    RecursiveAliases,
    CoordinateState
  >;

  from<K extends keyof G["nodes"] & string, A extends string>(
    kind: K,
    alias: UniqueAlias<A, Aliases>,
    options: { includeSubClasses: true },
  ): QueryBuilder<
    G,
    Aliases & Record<A, NodeAlias>,
    EdgeAliases,
    RecursiveAliases,
    CoordinateState
  >;

  from<K extends keyof G["nodes"] & string, A extends string>(
    kind: K | readonly K[],
    alias: UniqueAlias<A, Aliases>,
    options?: { includeSubClasses?: boolean },
  ): QueryBuilder<
    G,
    Aliases & Record<A, NodeAlias>,
    EdgeAliases,
    RecursiveAliases,
    CoordinateState
  > {
    validateQuerySource(this.#state, true);
    // Validate alias to prevent SQL injection
    validateSqlIdentifier(alias);
    const explicitKinds = typeof kind !== "string";
    if (explicitKinds && options !== undefined) {
      throw new ConfigurationError(
        "An explicit source kind list does not accept from() options.",
      );
    }
    const requestedKinds =
      typeof kind === "string" ? [kind] : [...new Set(kind)];
    if (requestedKinds.length === 0) {
      throw new ConfigurationError(
        "from() requires a nonempty source kind list.",
      );
    }
    for (const requestedKind of requestedKinds) {
      if (!this.#config.registry.hasNodeType(requestedKind)) {
        throw new KindNotFoundError(requestedKind, "node", {
          graphId: this.#config.graphId,
        });
      }
    }
    const includeSubClasses = options?.includeSubClasses ?? false;
    const kinds =
      typeof kind === "string" && includeSubClasses ?
        this.#config.registry.expandSubClasses(kind)
      : requestedKinds;

    const newState: QueryBuilderState = {
      ...this.#state,
      startAlias: alias,
      currentAlias: alias,
      startKinds: kinds,
      includeSubClasses,
    };

    return new QueryBuilder(this.#config, newState);
  }

  /**
   * Runtime-kind sibling of `from`; accepts a kind name or Store-issued token.
   * Throws `KindNotFoundError` if the kind is not registered. String-keyed
   * predicates use the `n.field("name").number().gte(...)` discriminator.
   */
  fromDynamic<T extends string | RuntimeNodeKind, A extends string>(
    kind: T,
    alias: UniqueAlias<A, Aliases>,
    options?: { includeSubClasses?: boolean },
  ): QueryBuilder<
    G,
    Aliases & Record<A, NodeAlias<DynamicNodeTypeFor<T>>>,
    EdgeAliases,
    RecursiveAliases,
    CoordinateState
  > {
    validateQuerySource(this.#state, true);
    validateSqlIdentifier(alias);
    const kindName = resolveRuntimeKindInput(
      kind,
      "node",
      getQueryBuilderInternalContext(this.#config).runtimeKindTokenResolver,
    );
    if (!this.#config.registry.hasNodeType(kindName)) {
      throw new KindNotFoundError(kindName, "node", {
        graphId: this.#config.graphId,
      });
    }

    const includeSubClasses = options?.includeSubClasses ?? false;
    const kinds =
      includeSubClasses ?
        this.#config.registry.expandSubClasses(kindName)
      : [kindName];

    const newState: QueryBuilderState = {
      ...this.#state,
      startAlias: alias,
      currentAlias: alias,
      startKinds: kinds,
      includeSubClasses,
      dynamicNodeAliases: new Set([...this.#state.dynamicNodeAliases, alias]),
    };

    return new QueryBuilder(this.#config, newState);
  }

  /** Stops a recursive branch at a matching endpoint; stopping endpoints are emitted by default. */
  stopExpansion<A extends keyof Aliases & string>(
    alias: A,
    build: (node: NodeAccessor<Aliases[A]["type"]>) => Predicate,
    options: Readonly<{ emitStopNode?: boolean }> = {},
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    const accessor = this.#createNodeAccessor(alias);
    const predicate = build(accessor as NodeAccessor<Aliases[A]["type"]>);
    return new QueryBuilder(
      this.#config,
      withRecursiveStopExpansion(
        this.#state,
        alias,
        predicate.__expr,
        options.emitStopNode ?? true,
      ),
    );
  }

  /** Filters completed match rows, preserving optional and recursive expansion semantics. */
  where(
    build: (
      context: QueryExpressionContext<G, Aliases, EdgeAliases, CoordinateState>,
    ) => DatabaseExpression<
      boolean | undefined,
      (keyof Aliases | keyof EdgeAliases) & string
    >,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    validateQuerySource(this.#state, false);
    const expression = build(this.#expressionContext());
    if (!isDatabaseExpression(expression))
      throw new ConfigurationError(
        "where() requires a Boolean database expression; ranked match helpers belong in whereNode().",
      );
    const predicate = this.#expressionPredicate(expression);
    const resultPredicate: PredicateExpression =
      this.#state.resultPredicate === undefined ?
        predicate
      : {
          __type: "and",
          predicates: [this.#state.resultPredicate, predicate],
        };
    return new QueryBuilder(this.#config, { ...this.#state, resultPredicate });
  }

  /**
   * Adds a WHERE clause for a node.
   */
  whereNode<A extends keyof Aliases & string>(
    alias: A,
    predicateFunction: (
      n: NodeAccessor<Aliases[A]["type"]>,
      expressions: QueryExpressionContext<
        G,
        Aliases,
        EdgeAliases,
        CoordinateState
      >,
    ) =>
      | Predicate
      | DatabaseExpression<
          boolean | undefined,
          (keyof Aliases | keyof EdgeAliases) & string
        >,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    const accessor = this.#createNodeAccessor(alias);
    const predicate = predicateFunction(
      accessor as NodeAccessor<Aliases[A]["type"]>,
      this.#expressionContext(),
    );

    const newState: QueryBuilderState = {
      ...this.#state,
      predicates: [
        ...this.#state.predicates,
        {
          targetAlias: alias,
          expression: this.#expressionPredicate(predicate),
        },
      ],
    };

    return new QueryBuilder(this.#config, newState);
  }

  /**
   * Adds a WHERE clause for an edge.
   *
   * @param alias - The edge alias to filter on
   * @param predicateFunction - A function that builds predicates using the edge accessor
   */
  whereEdge<EA extends keyof EdgeAliases & string>(
    alias: EA,
    predicateFunction: (
      edge: EdgeAccessor<EdgeAliases[EA]["type"]>,
      expressions: QueryExpressionContext<
        G,
        Aliases,
        EdgeAliases,
        CoordinateState
      >,
    ) =>
      | Predicate
      | DatabaseExpression<
          boolean | undefined,
          (keyof Aliases | keyof EdgeAliases) & string
        >,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    const accessor = this.#createEdgeAccessor(alias);
    const predicate = predicateFunction(
      accessor as EdgeAccessor<EdgeAliases[EA]["type"]>,
      this.#expressionContext(),
    );

    const newState: QueryBuilderState = {
      ...this.#state,
      predicates: [
        ...this.#state.predicates,
        {
          targetAlias: alias,
          targetType: "edge",
          expression: this.#expressionPredicate(predicate),
        },
      ],
    };

    return new QueryBuilder(this.#config, newState);
  }

  /**
   * Traverses an edge to another node (outgoing direction).
   *
   * By default, traverses from the current node (last traversal target, or start node).
   * Use the `from` option to traverse from a different alias (fan-out pattern).
   *
   * @param options.expand - Ontology expansion mode for implying/inverse edges
   * @param options.from - Alias to traverse from (defaults to current/last traversal target)
   */
  traverse<EK extends keyof G["edges"] & string, EA extends string>(
    edgeKind: EK,
    edgeAlias: EA,
    options?: {
      direction?: "out";
      expand?: TraversalExpansion;
      from?: keyof Aliases & string;
    } & IdentityTraversalOption<G>,
  ): TraversalBuilder<
    G,
    Aliases,
    EdgeAliases,
    EK,
    EA,
    "out",
    false,
    false,
    false,
    RecursiveAliases,
    CoordinateState
  >;

  /**
   * Traverses an edge to another node (incoming direction).
   *
   * By default, traverses from the current node (last traversal target, or start node).
   * Use the `from` option to traverse from a different alias (fan-out pattern).
   *
   * @param options.direction - Set to "in" for incoming edge traversal
   * @param options.expand - Ontology expansion mode for implying/inverse edges
   * @param options.from - Alias to traverse from (defaults to current/last traversal target)
   */
  traverse<EK extends keyof G["edges"] & string, EA extends string>(
    edgeKind: EK,
    edgeAlias: EA,
    options: {
      direction: "in";
      expand?: TraversalExpansion;
      from?: keyof Aliases & string;
    } & IdentityTraversalOption<G>,
  ): TraversalBuilder<
    G,
    Aliases,
    EdgeAliases,
    EK,
    EA,
    "in",
    false,
    false,
    false,
    RecursiveAliases,
    CoordinateState
  >;

  traverse<EK extends keyof G["edges"] & string, EA extends string>(
    edgeKind: EK,
    edgeAlias: EA,
    options?: {
      direction?: TraversalDirection;
      expand?: TraversalExpansion;
      from?: keyof Aliases & string;
    } & IdentityTraversalOption<G>,
  ): TraversalBuilder<
    G,
    Aliases,
    EdgeAliases,
    EK,
    EA,
    TraversalDirection,
    false,
    false,
    false,
    RecursiveAliases,
    CoordinateState
  > {
    // Validate edge alias to prevent SQL injection
    validateSqlIdentifier(edgeAlias);

    const direction = options?.direction ?? "out";
    this.#assertIdentityTraversalAllowed(options);
    const expansion = options?.expand ?? this.#config.defaultTraversalExpansion;
    validateQuerySource(this.#state, false);
    validateTraversalOptions(direction, expansion);
    const includeImplyingEdges =
      expansion === "implying" || expansion === "all";
    const includeInverseEdges = expansion === "inverse" || expansion === "all";
    // Use explicit `from` if provided, otherwise chain from currentAlias
    const fromAlias = options?.from ?? this.#state.currentAlias;

    // Expand edge kinds if including implying edges
    const edgeKinds = this.#expandTraversalEdgeKinds(
      edgeKind,
      includeImplyingEdges,
    );
    const inverseEdgeKinds =
      includeInverseEdges ?
        this.#expandInverseTraversalEdgeKinds(edgeKinds, includeImplyingEdges)
      : [];

    return new TraversalBuilder<
      G,
      Aliases,
      EdgeAliases,
      EK,
      EA,
      TraversalDirection,
      false,
      false,
      false,
      RecursiveAliases,
      CoordinateState
    >(
      this.#config,
      this.#state,
      edgeKinds,
      edgeAlias,
      direction,
      fromAlias,
      inverseEdgeKinds,
      false,
      undefined, // variableLength — default
      undefined, // pendingEdgePredicates — default
      options?.includeIdentityMembers ?? false,
    );
  }

  /**
   * Runtime-kind sibling of `traverse`; accepts a kind name or Store-issued
   * token. Throws `KindNotFoundError` if the edge kind is not registered.
   */
  traverseDynamic<T extends string | RuntimeEdgeKind, EA extends string>(
    edgeKind: T,
    edgeAlias: EA,
    options?: {
      direction?: TraversalDirection;
      expand?: TraversalExpansion;
      from?: keyof Aliases & string;
    } & IdentityTraversalOption<G>,
  ): TraversalBuilder<
    G,
    Aliases,
    EdgeAliases & Record<EA, EdgeAlias<DynamicEdgeTypeFor<T>>>,
    string,
    EA,
    TraversalDirection,
    false,
    false,
    false,
    RecursiveAliases,
    CoordinateState,
    DynamicEdgeTypeFor<T>
  > {
    return this.#beginDynamicTraversal(edgeKind, edgeAlias, false, options);
  }

  /**
   * Optionally traverses an edge to another node (LEFT JOIN semantics).
   * If no matching edge/node exists, the result will include null values.
   *
   * By default, traverses from the current node (last traversal target, or start node).
   * Use the `from` option to traverse from a different alias (fan-out pattern).
   *
   * @param options.direction - Direction of traversal: "out" (default) or "in"
   * @param options.expand - Ontology expansion mode for implying/inverse edges
   * @param options.from - Alias to traverse from (defaults to current/last traversal target)
   */
  optionalTraverse<EK extends keyof G["edges"] & string, EA extends string>(
    edgeKind: EK,
    edgeAlias: EA,
    options?: {
      direction?: "out";
      expand?: TraversalExpansion;
      from?: keyof Aliases & string;
    } & IdentityTraversalOption<G>,
  ): TraversalBuilder<
    G,
    Aliases,
    EdgeAliases,
    EK,
    EA,
    "out",
    true,
    false,
    false,
    RecursiveAliases,
    CoordinateState
  >;

  optionalTraverse<EK extends keyof G["edges"] & string, EA extends string>(
    edgeKind: EK,
    edgeAlias: EA,
    options: {
      direction: "in";
      expand?: TraversalExpansion;
      from?: keyof Aliases & string;
    } & IdentityTraversalOption<G>,
  ): TraversalBuilder<
    G,
    Aliases,
    EdgeAliases,
    EK,
    EA,
    "in",
    true,
    false,
    false,
    RecursiveAliases,
    CoordinateState
  >;

  optionalTraverse<EK extends keyof G["edges"] & string, EA extends string>(
    edgeKind: EK,
    edgeAlias: EA,
    options?: {
      direction?: TraversalDirection;
      expand?: TraversalExpansion;
      from?: keyof Aliases & string;
    } & IdentityTraversalOption<G>,
  ): TraversalBuilder<
    G,
    Aliases,
    EdgeAliases,
    EK,
    EA,
    TraversalDirection,
    true,
    false,
    false,
    RecursiveAliases,
    CoordinateState
  > {
    // Validate edge alias to prevent SQL injection
    validateSqlIdentifier(edgeAlias);

    const direction = options?.direction ?? "out";
    this.#assertIdentityTraversalAllowed(options);
    const expansion = options?.expand ?? this.#config.defaultTraversalExpansion;
    validateQuerySource(this.#state, false);
    validateTraversalOptions(direction, expansion);
    const includeImplyingEdges =
      expansion === "implying" || expansion === "all";
    const includeInverseEdges = expansion === "inverse" || expansion === "all";
    // Use explicit `from` if provided, otherwise chain from currentAlias
    const fromAlias = options?.from ?? this.#state.currentAlias;

    // Expand edge kinds if including implying edges
    const edgeKinds = this.#expandTraversalEdgeKinds(
      edgeKind,
      includeImplyingEdges,
    );
    const inverseEdgeKinds =
      includeInverseEdges ?
        this.#expandInverseTraversalEdgeKinds(edgeKinds, includeImplyingEdges)
      : [];

    return new TraversalBuilder<
      G,
      Aliases,
      EdgeAliases,
      EK,
      EA,
      TraversalDirection,
      true,
      false,
      false,
      RecursiveAliases,
      CoordinateState
    >(
      this.#config,
      this.#state,
      edgeKinds,
      edgeAlias,
      direction,
      fromAlias,
      inverseEdgeKinds,
      true,
      undefined, // variableLength — default
      undefined, // pendingEdgePredicates — default
      options?.includeIdentityMembers ?? false,
    );
  }

  /**
   * Runtime-kind sibling of `optionalTraverse`; accepts a kind name or
   * Store-issued token. LEFT JOIN semantics — non-matching rows produce a null
   * edge alias instead of dropping. Throws `KindNotFoundError` if the edge kind
   * is not registered.
   */
  optionalTraverseDynamic<
    T extends string | RuntimeEdgeKind,
    EA extends string,
  >(
    edgeKind: T,
    edgeAlias: EA,
    options?: {
      direction?: TraversalDirection;
      expand?: TraversalExpansion;
      from?: keyof Aliases & string;
    } & IdentityTraversalOption<G>,
  ): TraversalBuilder<
    G,
    Aliases,
    EdgeAliases & Record<EA, EdgeAlias<DynamicEdgeTypeFor<T>, true>>,
    string,
    EA,
    TraversalDirection,
    true,
    false,
    false,
    RecursiveAliases,
    CoordinateState,
    DynamicEdgeTypeFor<T>
  > {
    return this.#beginDynamicTraversal(edgeKind, edgeAlias, true, options);
  }

  /**
   * Shared body for `traverseDynamic` and `optionalTraverseDynamic`.
   * The only difference is the `optional` flag passed to the
   * `TraversalBuilder` constructor.
   */
  #beginDynamicTraversal<
    T extends string | RuntimeEdgeKind,
    EA extends string,
    Optional extends boolean,
  >(
    edgeKind: T,
    edgeAlias: EA,
    optional: Optional,
    options:
      | ({
          direction?: TraversalDirection;
          expand?: TraversalExpansion;
          from?: keyof Aliases & string;
          includeIdentityMembers?: boolean;
        } & IdentityTraversalOption<G>)
      | undefined,
  ): TraversalBuilder<
    G,
    Aliases,
    EdgeAliases & Record<EA, EdgeAlias<DynamicEdgeTypeFor<T>, Optional>>,
    string,
    EA,
    TraversalDirection,
    Optional,
    false,
    false,
    RecursiveAliases,
    CoordinateState,
    DynamicEdgeTypeFor<T>
  > {
    validateSqlIdentifier(edgeAlias);
    const edgeKindName = resolveRuntimeKindInput(
      edgeKind,
      "edge",
      getQueryBuilderInternalContext(this.#config).runtimeKindTokenResolver,
    );
    if (!this.#config.registry.hasEdgeType(edgeKindName)) {
      throw new KindNotFoundError(edgeKindName, "edge", {
        graphId: this.#config.graphId,
      });
    }

    const direction = options?.direction ?? "out";
    this.#assertIdentityTraversalAllowed(options);
    const expansion = options?.expand ?? this.#config.defaultTraversalExpansion;
    validateQuerySource(this.#state, false);
    validateTraversalOptions(direction, expansion);
    const includeImplyingEdges =
      expansion === "implying" || expansion === "all";
    const includeInverseEdges = expansion === "inverse" || expansion === "all";
    const fromAlias = options?.from ?? this.#state.currentAlias;

    const edgeKinds = this.#expandTraversalEdgeKinds(
      edgeKindName,
      includeImplyingEdges,
    );
    const inverseEdgeKinds =
      includeInverseEdges ?
        this.#expandInverseTraversalEdgeKinds(edgeKinds, includeImplyingEdges)
      : [];

    const newState: QueryBuilderState = {
      ...this.#state,
      dynamicEdgeAliases: new Set([
        ...this.#state.dynamicEdgeAliases,
        edgeAlias,
      ]),
    };

    return new TraversalBuilder<
      G,
      Aliases,
      EdgeAliases & Record<EA, EdgeAlias<DynamicEdgeTypeFor<T>, Optional>>,
      string,
      EA,
      TraversalDirection,
      Optional,
      false,
      false,
      RecursiveAliases,
      CoordinateState,
      DynamicEdgeTypeFor<T>
    >(
      this.#config,
      newState,
      edgeKinds,
      edgeAlias,
      direction,
      fromAlias,
      inverseEdgeKinds,
      optional,
      undefined, // variableLength — default
      undefined, // pendingEdgePredicates — default
      options?.includeIdentityMembers ?? false,
    );
  }

  /** @internal Identifies this query's lexical expression scope. */
  getExpressionScopeIdentity(): symbol {
    return getExpressionScope(this.#config);
  }

  #expressionPredicate(
    predicate: Predicate | DatabaseExpression<boolean | undefined>,
  ): PredicateExpression {
    if ("__expr" in predicate) return predicate.__expr;
    assertExpressionScope(predicate, this.getExpressionScopeIdentity());
    if (predicate.valueType !== "boolean")
      throw new ConfigurationError(
        "A predicate requires a Boolean database expression.",
      );
    return { __type: "database_expression_predicate", expression: predicate };
  }

  #expressionContext(): QueryExpressionContext<
    G,
    Aliases,
    EdgeAliases,
    CoordinateState
  > {
    const aliases = createExpressionAliasContext<Aliases, EdgeAliases>(
      this.#config,
      this.#state,
    );
    const helpers = createExpressionSubqueryHelpers<
      QueryBuilder<
        G,
        EmptyAliasMap,
        EmptyEdgeAliasMap,
        EmptyRecursiveAliasMap,
        CoordinateState
      >,
      ExpressionAliasContext<Aliases, EdgeAliases, never>,
      (keyof Aliases | keyof EdgeAliases) & string
    >({
      parentScopeIdentity: this.getExpressionScopeIdentity(),
      parentProvenance: {
        graphId: this.#config.graphId,
        executionTarget:
          this.#config.backend === undefined ?
            undefined
          : backendDerivationRoot(this.#config.backend),
      },
      parentCoordinate: buildQueryAst(this.#config, this.#state),
      createSubquery: () => {
        const { resultPredicate: _resultPredicate, ...subqueryState } =
          this.#state;
        const config = { ...this.#config };
        registerQueryBuilderInternalContext(
          config,
          getQueryBuilderInternalContext(this.#config),
        );
        return new QueryBuilder(config, {
          ...subqueryState,
          startAlias: "",
          currentAlias: "",
          startKinds: [],
          traversals: [],
          predicates: [],
          projection: [],
          orderBy: [],
          aggregateOrderBy: [],
          groupBy: undefined,
          having: undefined,
          limit: undefined,
          offset: undefined,
          fusion: undefined,
          dynamicNodeAliases: new Set(),
          dynamicEdgeAliases: new Set(),
        });
      },
      createOuterContext: (childScope) =>
        createExpressionAliasContext<Aliases, EdgeAliases, never>(
          this.#config,
          this.#state,
          (expression) =>
            createOuterReferenceExpression(expression, childScope),
        ),
    });
    return new Proxy(helpers, {
      get(target, key, receiver) {
        if (key === "$exists" || key === "$scalar")
          return Reflect.get(target, key, receiver);
        return Reflect.get(aliases, key);
      },
    }) as QueryExpressionContext<G, Aliases, EdgeAliases, CoordinateState>;
  }

  /** Projects database expressions; the callback runs once when building the query. */
  project<
    const Fields extends Readonly<
      Record<
        string,
        DatabaseExpression<
          unknown,
          (keyof Aliases | keyof EdgeAliases) & string
        >
      >
    >,
  >(
    build: (
      context: QueryExpressionContext<G, Aliases, EdgeAliases, CoordinateState>,
    ) => Fields,
  ): ExecutableProjectionQuery<
    Fields,
    QueryExpressionContext<G, Aliases, EdgeAliases, CoordinateState>
  > {
    return this.#project(build(this.#expressionContext()));
  }

  #project<Fields extends DatabaseProjection>(
    fields: Fields,
  ): ExecutableProjectionQuery<
    Fields,
    QueryExpressionContext<G, Aliases, EdgeAliases, CoordinateState>
  > {
    if (Object.getOwnPropertySymbols(fields).length > 0)
      throw new ConfigurationError("Projection output names must be strings.");
    const entries = Object.entries(fields);
    if (entries.length === 0)
      throw new ConfigurationError(
        "project() requires at least one database expression.",
      );
    const projection = entries.map(([outputName, source]) => {
      validateSqlIdentifier(outputName);
      if (
        outputName.startsWith("__tg_") ||
        outputName === "typegraphschemaversion"
      )
        throw new ConfigurationError("Projection output name is reserved.");
      if (!isDatabaseExpression(source))
        throw new ConfigurationError(
          "project() accepts database expressions; use expr.literal() for constants.",
        );
      assertExpressionScope(source, this.getExpressionScopeIdentity());
      return { outputName, source };
    });
    return new ExecutableProjectionQuery(
      this.#config,
      { ...this.#state, projection },
      fields,
      () => this.#expressionContext(),
    );
  }

  /** Counts match rows (or groups), preserving offset and limit. */
  count(): Promise<number> {
    return executeQueryTerminal(this.#config, this.#state, "count");
  }

  /** Tests whether the bounded relation contains a row. */
  async exists(): Promise<boolean> {
    return (
      (await executeQueryTerminal(this.#config, this.#state, "exists")) > 0
    );
  }

  /**
   * Selects fields to return.
   */
  select<R>(
    selectFunction: (
      context: SelectContext<Aliases, EdgeAliases, RecursiveAliases>,
    ) => R,
  ): ExecutableQuery<G, Aliases, EdgeAliases, RecursiveAliases, R> {
    // For now, project all fields from all aliases
    // A more sophisticated implementation would parse the selectFn

    // Start node fields (including metadata)
    const startFields = buildNodeFields(this.#state.startAlias);

    // Traversal node and edge fields (including metadata)
    // Edge fields are in the node's CTE, so we pass the node alias for CTE reference
    const traversalFields = this.#state.traversals.flatMap((traversal) => [
      ...buildEdgeFields(traversal.edgeAlias, traversal.nodeAlias),
      ...buildNodeFields(traversal.nodeAlias),
    ]);

    const projection = [...startFields, ...traversalFields];

    const newState: QueryBuilderState = {
      ...this.#state,
      projection,
    };

    return new ExecutableQuery(this.#config, newState, selectFunction);
  }

  /**
   * Selects fields including aggregates.
   * Use with groupBy() for aggregate queries.
   *
   * @param fields - Object mapping output names to field refs or aggregate expressions
   */
  aggregate<
    const Fields extends Readonly<
      Record<
        string,
        DatabaseExpression<
          unknown,
          (keyof Aliases | keyof EdgeAliases) & string
        >
      >
    >,
  >(
    build: (
      context: QueryExpressionContext<G, Aliases, EdgeAliases, CoordinateState>,
    ) => Fields,
  ): ExecutableProjectionQuery<
    Fields,
    QueryExpressionContext<G, Aliases, EdgeAliases, CoordinateState>
  >;
  aggregate<R extends Record<string, FieldRef | AggregateExpr>>(
    fields: R,
  ): ExecutableAggregateQuery<G, Aliases & EdgeAliases, R>;
  aggregate<
    R extends Record<string, FieldRef | AggregateExpr>,
    Fields extends DatabaseProjection,
  >(
    fields:
      | R
      | ((
          context: QueryExpressionContext<
            G,
            Aliases,
            EdgeAliases,
            CoordinateState
          >,
        ) => Fields),
  ):
    | ExecutableAggregateQuery<G, Aliases & EdgeAliases, R>
    | ExecutableProjectionQuery<
        Fields,
        QueryExpressionContext<G, Aliases, EdgeAliases, CoordinateState>
      > {
    if (typeof fields === "function")
      return this.#project(fields(this.#expressionContext()));

    const resolvedFields = Object.fromEntries(
      Object.entries(fields).map(([outputName, source]) => {
        const fieldRef = source.__type === "aggregate" ? source.field : source;

        if (
          fieldRef.path.length !== 1 ||
          fieldRef.path[0] !== "props" ||
          fieldRef.jsonPointer === undefined
        ) {
          return [outputName, source];
        }

        const segments = parseJsonPointer(fieldRef.jsonPointer);
        if (segments.length === 0) {
          return [outputName, source];
        }

        const nodeKindNames = this.#getKindNamesForAlias(fieldRef.alias);
        const edgeKindNames = this.#getEdgeKindNamesForAlias(fieldRef.alias);
        const typeInfo = resolveAggregateFieldTypeInfo(
          this.#config.schemaIntrospector,
          nodeKindNames,
          edgeKindNames,
          segments,
        );

        if (!typeInfo) {
          if (source.__type === "aggregate") validateAggregateOperand(source);
          return [outputName, source];
        }

        if (source.__type === "aggregate") {
          validateAggregateOperand(source, typeInfo.valueType);
        }

        const resolvedField = {
          ...fieldRef,
          valueType: typeInfo.valueType,
          elementType: typeInfo.elementType,
        } satisfies FieldRef;

        return source.__type === "aggregate" ?
            [outputName, { ...source, field: resolvedField }]
          : [outputName, resolvedField];
      }),
    ) as R;

    const projection: ProjectedField[] = Object.entries(resolvedFields).map(
      ([outputName, source]) => {
        const sourceAlias =
          source.__type === "aggregate" ? source.field.alias : source.alias;
        const edgeTraversal = this.#state.traversals.find(
          (traversal) => traversal.edgeAlias === sourceAlias,
        );
        return edgeTraversal === undefined ?
            { outputName, source }
          : {
              outputName,
              source,
              cteAlias: `cte_${edgeTraversal.nodeAlias}`,
            };
      },
    );

    const newState: QueryBuilderState = {
      ...this.#state,
      projection,
    };

    return new ExecutableAggregateQuery(this.#config, newState, resolvedFields);
  }

  /**
   * Orders results.
   */
  orderBy(
    build: (
      context: QueryExpressionContext<G, Aliases, EdgeAliases, CoordinateState>,
    ) => DatabaseExpression<
      unknown,
      (keyof Aliases | keyof EdgeAliases) & string
    >,
    direction?: SortDirection,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState>;
  orderBy<A extends (keyof Aliases | keyof EdgeAliases) & string>(
    alias: A,
    field: string,
    direction?: SortDirection,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState>;
  orderBy(
    alias:
      | ((
          context: QueryExpressionContext<
            G,
            Aliases,
            EdgeAliases,
            CoordinateState
          >,
        ) => DatabaseExpression)
      | ((keyof Aliases | keyof EdgeAliases) & string),
    field?: string,
    direction: SortDirection = "asc",
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    if (typeof alias === "function") {
      const expression = alias(this.#expressionContext());
      assertExpressionScope(expression, this.getExpressionScopeIdentity());
      const sort = field ?? "asc";
      validateSortDirection(sort);
      return new QueryBuilder(this.#config, {
        ...this.#state,
        orderBy: [
          ...this.#state.orderBy,
          { field: expression, direction: sort },
        ],
      });
    }
    if (field === undefined)
      throw new ConfigurationError("orderBy() requires a field.");
    validateSortDirection(direction);
    const edgeKindNames = this.#getEdgeKindNamesForAlias(alias);
    const isEdge = edgeKindNames !== undefined;
    const nodeKindNames =
      isEdge ? undefined : this.#getKindNamesForAlias(alias);
    const hasDeclaredProperty =
      isEdge ?
        this.#config.schemaIntrospector.hasDeclaredEdgeField(
          edgeKindNames,
          field,
        )
      : nodeKindNames !== undefined &&
        this.#config.schemaIntrospector.hasDeclaredField(nodeKindNames, field);
    const systemField = resolveSystemOrderField(
      alias,
      field,
      isEdge,
      hasDeclaredProperty,
    );
    let typeInfo: FieldTypeInfo | undefined;
    if (systemField === undefined) {
      typeInfo =
        isEdge ?
          this.#config.schemaIntrospector.getSharedEdgeFieldTypeInfo(
            edgeKindNames,
            field,
          )
        : nodeKindNames === undefined ? undefined
        : this.#config.schemaIntrospector.getSharedFieldTypeInfo(
            nodeKindNames,
            field,
          );
      assertSharedNodeField(nodeKindNames, field, typeInfo);
    }
    const orderSpec = buildOrderSpec(
      alias,
      field,
      direction,
      systemField,
      typeInfo,
    );

    const newState: QueryBuilderState = {
      ...this.#state,
      orderBy: [...this.#state.orderBy, orderSpec],
    };

    return new QueryBuilder(this.#config, newState);
  }

  /**
   * Limits the number of results.
   */
  limit(
    n: number,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    validateQueryRange(n, "limit");
    return new QueryBuilder(this.#config, {
      ...this.#state,
      limit: n,
    });
  }

  /**
   * Offsets the results.
   */
  offset(
    n: number,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    validateQueryRange(n, "offset");
    return new QueryBuilder(this.#config, {
      ...this.#state,
      offset: n,
    });
  }

  /**
   * Groups results by the specified field.
   * Use with aggregate functions like COUNT, SUM, AVG in select().
   *
   * @param alias - The node alias to group by
   * @param field - The field name to group by
   */
  groupBy(
    build: (
      context: QueryExpressionContext<G, Aliases, EdgeAliases, CoordinateState>,
    ) =>
      | DatabaseExpression<
          unknown,
          (keyof Aliases | keyof EdgeAliases) & string
        >
      | readonly DatabaseExpression<
          unknown,
          (keyof Aliases | keyof EdgeAliases) & string
        >[],
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState>;
  groupBy<A extends keyof Aliases & string>(
    alias: A,
    field: string,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState>;
  groupBy(
    alias:
      | (keyof Aliases & string)
      | ((
          context: QueryExpressionContext<
            G,
            Aliases,
            EdgeAliases,
            CoordinateState
          >,
        ) => DatabaseExpression | readonly DatabaseExpression[]),
    field?: string,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    if (typeof alias === "function") {
      const value = alias(this.#expressionContext());
      const fields: readonly DatabaseExpression[] =
        Array.isArray(value) ? value : [value as DatabaseExpression];
      if (fields.length === 0)
        throw new ConfigurationError(
          "groupBy() requires at least one database expression.",
        );
      for (const expression of fields)
        assertExpressionScope(expression, this.getExpressionScopeIdentity());
      return new QueryBuilder(this.#config, {
        ...this.#state,
        groupBy: {
          fields: [...(this.#state.groupBy?.fields ?? []), ...fields],
        },
      });
    }
    if (field === undefined)
      throw new ConfigurationError("groupBy() requires a field.");
    const kindNames = this.#getKindNamesForAlias(alias);
    const typeInfo =
      kindNames ?
        this.#config.schemaIntrospector.getSharedFieldTypeInfo(kindNames, field)
      : undefined;
    assertSharedNodeField(kindNames, field, typeInfo);

    const fieldRefValue: FieldRef = {
      __type: "field_ref",
      alias,
      path: ["props"],
      jsonPointer: jsonPointer([field]),
      valueType: typeInfo?.valueType,
      elementType: typeInfo?.elementType,
    };

    const existingFields = this.#state.groupBy?.fields ?? [];
    const newGroupBy: GroupBySpec = {
      fields: [...existingFields, fieldRefValue],
    };

    return new QueryBuilder(this.#config, {
      ...this.#state,
      groupBy: newGroupBy,
    });
  }

  /**
   * Groups results by the node ID.
   * Use when you want to group by a complete node rather than a specific field.
   *
   * @param alias - The node alias to group by (uses the node's ID)
   */
  groupByNode<A extends keyof Aliases & string>(
    alias: A,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    const fieldRefValue: FieldRef = {
      __type: "field_ref",
      alias,
      path: ["id"],
      valueType: "string",
    };

    const existingFields = this.#state.groupBy?.fields ?? [];
    const newGroupBy: GroupBySpec = {
      fields: [...existingFields, fieldRefValue],
    };

    return new QueryBuilder(this.#config, {
      ...this.#state,
      groupBy: newGroupBy,
    });
  }

  /**
   * Filters grouped results using aggregate conditions (HAVING clause).
   * Use after groupBy() to filter based on aggregate values.
   *
   * @param predicate - A predicate expression to filter groups
   */
  having(
    predicateOrBuild:
      | PredicateExpression
      | ((
          context: QueryExpressionContext<
            G,
            Aliases,
            EdgeAliases,
            CoordinateState
          >,
        ) => DatabaseExpression<
          boolean | undefined,
          (keyof Aliases | keyof EdgeAliases) & string
        >),
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    const predicate =
      typeof predicateOrBuild === "function" ?
        this.#expressionPredicate(predicateOrBuild(this.#expressionContext()))
      : predicateOrBuild;
    return new QueryBuilder(this.#config, {
      ...this.#state,
      having:
        this.#state.having === undefined ?
          predicate
        : { __type: "and", predicates: [this.#state.having, predicate] },
    });
  }

  /**
   * Sets fusion parameters for hybrid (vector + fulltext) queries.
   *
   * Applies only when the query contains both a `.similarTo()` and a
   * `.$fulltext.matches()` predicate. Without this call, the default is
   * RRF with k=60 and equal weights. A mismatch between `.fuseWith()`
   * configuration and the predicates on the query is caught during
   * compilation, not here.
   *
   * @example
   * ```typescript
   * store.query()
   *   .from("Document", "d")
   *   .whereNode("d", d =>
   *     d.$fulltext.matches("renewable energy", 50)
   *       .and(d.embedding.similarTo(vec, 50))
   *       .and(d.tenantId.eq(tenant))
   *   )
   *   .fuseWith({ k: 60, weights: { fulltext: 1.5 } })
   *   .limit(10)
   *   .execute();
   * ```
   */
  fuseWith(
    options: HybridFusionOptions,
  ): QueryBuilder<G, Aliases, EdgeAliases, RecursiveAliases, CoordinateState> {
    validateHybridFusionOptions(options);
    return new QueryBuilder(this.#config, {
      ...this.#state,
      fusion: options,
    });
  }

  /**
   * Applies a query fragment to transform this builder.
   *
   * Fragments are reusable query transformations that can add predicates,
   * traversals, ordering, and other query operations. Use this for
   * composing complex queries from simpler, reusable parts.
   *
   * @example
   * ```typescript
   * // Define a reusable fragment
   * const activeUsers = createFragment<MyGraph>()((q) =>
   *   q.whereNode("u", ({ status }) => status.eq("active"))
   * );
   *
   * // Apply the fragment
   * const results = await query()
   *   .from("User", "u")
   *   .pipe(activeUsers)
   *   .select((ctx) => ctx.u)
   *   .execute();
   * ```
   *
   * @param fragment - A function that transforms the builder
   * @returns The transformed builder
   */
  pipe<
    OutAliases extends AliasMap,
    OutEdgeAliases extends EdgeAliasMap = EdgeAliases,
    OutRecAliases extends RecursiveAliasMap = RecursiveAliases,
  >(
    fragment: (
      builder: QueryBuilder<
        G,
        Aliases,
        EdgeAliases,
        RecursiveAliases,
        CoordinateState
      >,
    ) => QueryBuilder<
      G,
      OutAliases,
      OutEdgeAliases,
      OutRecAliases,
      CoordinateState
    >,
  ): QueryBuilder<
    G,
    OutAliases,
    OutEdgeAliases,
    OutRecAliases,
    CoordinateState
  > {
    return fragment(this);
  }

  /**
   * Gets all kind names for an alias.
   */
  #getKindNamesForAlias(alias: string): readonly string[] | undefined {
    if (alias === this.#state.startAlias) {
      return this.#state.startKinds;
    }
    for (const traversal of this.#state.traversals) {
      if (traversal.nodeAlias === alias) {
        return traversal.nodeKinds;
      }
    }
    return undefined;
  }

  /**
   * Determines the appropriate field builder based on Zod schema type.
   */
  #getFieldBuilderForProperty(
    kindNames: readonly string[] | undefined,
    property: string,
    alias: string,
  ): BaseFieldAccessor {
    const typeInfo =
      kindNames ?
        this.#config.schemaIntrospector.getSharedFieldTypeInfo(
          kindNames,
          property,
        )
      : undefined;

    assertSharedNodeField(kindNames, property, typeInfo);
    const ref = fieldRef(alias, ["props"], {
      jsonPointer: jsonPointer([property]),
      valueType: typeInfo?.valueType,
      elementType: typeInfo?.elementType,
    });

    return this.#buildFieldBuilderForTypeInfo(ref, typeInfo);
  }

  #buildFieldBuilderForTypeInfo(
    ref: ReturnType<typeof fieldRef>,
    typeInfo: FieldTypeInfo | undefined,
  ): BaseFieldAccessor {
    return buildFieldBuilderForTypeInfo(ref, typeInfo);
  }

  #createNodeAccessor(alias: string): NodeAccessor<NodeType> {
    const kindNames = this.#getKindNamesForAlias(alias);
    const idAccessor = stringField(
      fieldRef(alias, ["id"], { valueType: "string" }),
    );
    const kindAccessor = stringField(
      fieldRef(alias, ["kind"], { valueType: "string" }),
    );
    const fulltextAccessor = createFulltextAccessor(alias, () =>
      this.#hasSearchableField(kindNames),
    );

    if (this.#state.dynamicNodeAliases.has(alias)) {
      return {
        id: idAccessor,
        kind: kindAccessor,
        $fulltext: fulltextAccessor,
        field: (name: string) =>
          createDynamicFieldBuilder(
            this.#config.schemaIntrospector,
            alias,
            name,
            kindNames,
            "node",
          ),
      } as unknown as NodeAccessor<NodeType>;
    }

    // Use a Proxy to provide flattened property access
    return new Proxy({} as NodeAccessor<NodeType>, {
      get: (_, property: string | symbol) => {
        // Handle symbols and special properties to avoid infinite loops
        if (typeof property === "symbol") return;

        // System fields
        if (property === "id") return idAccessor;
        if (property === "kind") return kindAccessor;
        if (property === "$fulltext") return fulltextAccessor;

        // A DECLARED field wins over the interop exemption: `toJSON` and `then`
        // are legal schema field names, and the accessor type offers them, so
        // resolving them to `undefined` here made a declared field
        // unaddressable in a predicate. Only an UNDECLARED probe resolves to
        // `undefined`, keeping the accessor safe to await or stringify.
        if (
          isInteropProbeKey(property) &&
          !(
            kindNames !== undefined &&
            this.#config.schemaIntrospector.hasDeclaredField(
              kindNames,
              property,
            )
          )
        ) {
          return;
        }

        // Schema properties
        return this.#getFieldBuilderForProperty(kindNames, property, alias);
      },
    });
  }

  #hasSearchableField(kindNames: readonly string[] | undefined): boolean {
    if (!kindNames) return false;
    return this.#config.schemaIntrospector.hasSearchableField(kindNames);
  }

  /**
   * Guards `includeIdentityMembers` against a non-identity-enabled builder.
   */
  #assertIdentityTraversalAllowed(
    options: Readonly<{ includeIdentityMembers?: boolean }> | undefined,
  ): void {
    if (!options?.includeIdentityMembers || this.#config.identityEnabled) {
      return;
    }
    throw new ConfigurationError(
      "includeIdentityMembers requires an identity-enabled graph registry.",
      { code: "IDENTITY_NOT_ENABLED", graphId: this.#config.graphId },
      {
        suggestion:
          "Enable defineGraph(...).identity and build the registry from that graph.",
      },
    );
  }

  #expandTraversalEdgeKinds(
    edgeKind: string,
    includeImplyingEdges: boolean,
  ): readonly string[] {
    return includeImplyingEdges ?
        this.#config.registry.expandImplyingEdges(edgeKind)
      : [edgeKind];
  }

  #expandInverseTraversalEdgeKinds(
    edgeKinds: readonly string[],
    includeImplyingEdges: boolean,
  ): readonly string[] {
    const inverseKinds = new Set<string>();

    for (const kind of edgeKinds) {
      const inverseKind = this.#config.registry.getInverseEdge(kind);
      if (inverseKind === undefined) {
        continue;
      }

      inverseKinds.add(inverseKind);

      if (!includeImplyingEdges) {
        continue;
      }

      for (const implyingKind of this.#config.registry.expandImplyingEdges(
        inverseKind,
      )) {
        inverseKinds.add(implyingKind);
      }
    }

    return [...inverseKinds];
  }

  /**
   * Gets edge kind names for an edge alias.
   */
  #getEdgeKindNamesForAlias(alias: string): readonly string[] | undefined {
    for (const traversal of this.#state.traversals) {
      if (traversal.edgeAlias === alias) {
        return mergeEdgeKinds(traversal);
      }
    }
    return undefined;
  }

  /**
   * Determines the appropriate field builder for an edge property based on Zod schema type.
   */
  #getFieldBuilderForEdgeProperty(
    edgeKindNames: readonly string[] | undefined,
    property: string,
    alias: string,
  ): BaseFieldAccessor {
    const typeInfo =
      edgeKindNames ?
        this.#config.schemaIntrospector.getSharedEdgeFieldTypeInfo(
          edgeKindNames,
          property,
        )
      : undefined;

    const ref = fieldRef(alias, ["props"], {
      jsonPointer: jsonPointer([property]),
      valueType: typeInfo?.valueType,
      elementType: typeInfo?.elementType,
    });

    return this.#buildFieldBuilderForTypeInfo(ref, typeInfo);
  }

  #createEdgeAccessor(alias: string): EdgeAccessor<EdgeType> {
    const edgeKindNames = this.#getEdgeKindNamesForAlias(alias);
    const idAccessor = stringField(
      fieldRef(alias, ["id"], { valueType: "string" }),
    );
    const kindAccessor = stringField(
      fieldRef(alias, ["kind"], { valueType: "string" }),
    );
    const fromIdAccessor = stringField(
      fieldRef(alias, ["from_id"], { valueType: "string" }),
    );
    const toIdAccessor = stringField(
      fieldRef(alias, ["to_id"], { valueType: "string" }),
    );

    if (this.#state.dynamicEdgeAliases.has(alias)) {
      return {
        id: idAccessor,
        kind: kindAccessor,
        fromId: fromIdAccessor,
        toId: toIdAccessor,
        field: (name: string) =>
          createDynamicFieldBuilder(
            this.#config.schemaIntrospector,
            alias,
            name,
            edgeKindNames,
            "edge",
          ),
      } as unknown as EdgeAccessor<EdgeType>;
    }

    // Use a Proxy to provide flattened property access
    return new Proxy({} as EdgeAccessor<EdgeType>, {
      get: (_, property: string | symbol) => {
        // Handle symbols and special properties to avoid infinite loops
        if (typeof property === "symbol") return;

        // System fields
        if (property === "id") return idAccessor;
        if (property === "kind") return kindAccessor;
        if (property === "fromId") return fromIdAccessor;
        if (property === "toId") return toIdAccessor;

        // A DECLARED field wins over the interop exemption — see
        // #createNodeAccessor.
        if (
          isInteropProbeKey(property) &&
          !(
            edgeKindNames !== undefined &&
            this.#config.schemaIntrospector.hasDeclaredEdgeField(
              edgeKindNames,
              property,
            )
          )
        ) {
          return;
        }

        // Schema properties
        return this.#getFieldBuilderForEdgeProperty(
          edgeKindNames,
          property,
          alias,
        );
      },
    });
  }
}
